import React from "react";
import { CustomChip } from "@ippon/shared-components";
import BuildIcon from "@mui/icons-material/Build";
import BoltIcon from "@mui/icons-material/Bolt";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import DescriptionIcon from "@mui/icons-material/Description";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import ApartmentIcon from "@mui/icons-material/Apartment";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

export const EXPENSE_GROUP_FALLBACK = "その他";

/**
 * 支出カテゴリの共通定義 (ExpenseListTab / RecurringExpenseTab / Repair 等で共用)
 *
 * TODO: 融資返済 (loan) / 修繕積立 (reserve) は機能実装時に追加予定。
 */
export const EXPENSE_CATEGORIES = [
  { key: "all", label: "すべて", icon: null },
  { key: "repair", label: "工事費", icon: <BuildIcon sx={{ fontSize: 14 }} /> },
  {
    key: "utility",
    label: "水光熱費",
    icon: <BoltIcon sx={{ fontSize: 14 }} />,
  },
  {
    key: "tax",
    label: "固定資産税・都市計画税",
    icon: <AccountBalanceIcon sx={{ fontSize: 14 }} />,
  },
  {
    key: "insurance",
    label: "保険料",
    icon: <DescriptionIcon sx={{ fontSize: 14 }} />,
  },
  {
    key: "amManagement",
    label: "PM管理費",
    icon: <HomeWorkIcon sx={{ fontSize: 14 }} />,
  },
  {
    key: "bmManagement",
    label: "BM管理費",
    icon: <ApartmentIcon sx={{ fontSize: 14 }} />,
  },
  {
    key: "other",
    label: "その他",
    icon: <MoreHorizIcon sx={{ fontSize: 14 }} />,
  },
] as const;

/**
 * 支出カテゴリの defaults (form セレクト / CardTab 共通)。
 * - FE がデフォルトの正、BE 側 (`CategoryManagement.expense_categories`) は
 *   ユーザーが追加した custom 文字列のみを持つ。
 * - "all" を含まない (form セレクト側では all を使わないため)。CardTab 側で
 *   先頭に「すべて」を別途差し込む。
 */
export const DEFAULT_EXPENSE_CATEGORY_OPTIONS: ReadonlyArray<{
  value: string;
  label: string;
}> = EXPENSE_CATEGORIES.filter((c) => c.key !== "all").map((c) => ({
  value: c.key,
  label: c.label,
}));

/**
 * カテゴリ名 (BudgetCategory.name または旧 ExpenseRecord.category) から
 * アイコン/色のテーマを解決する。
 *
 * - キーワードベースの heuristic マッチ (BudgetCategories は client が増やせるので
 *   完全一致 map ではなく substring 判定にしている)
 * - マッチしないカテゴリは "その他" (グレー) のテーマで表示
 */
export type CategoryTheme = {
  icon: React.ReactNode;
  bg: string;
  color: string;
};

const THEME_RULES: ReadonlyArray<{
  keywords: string[];
  theme: CategoryTheme;
}> = [
  {
    keywords: ["修繕", "repair", "リノベ"],
    theme: {
      icon: <BuildIcon sx={{ fontSize: 16 }} />,
      bg: "#FFF3E0",
      color: "#E65100",
    },
  },
  {
    keywords: ["水道", "光熱", "電気", "ガス", "utility"],
    theme: {
      icon: <BoltIcon sx={{ fontSize: 16 }} />,
      bg: "#FFFDE7",
      color: "#F57F17",
    },
  },
  {
    keywords: ["税", "固定資産", "tax"],
    theme: {
      icon: <AccountBalanceIcon sx={{ fontSize: 16 }} />,
      bg: "#E3F2FD",
      color: "#1565C0",
    },
  },
  {
    keywords: ["保険", "insurance"],
    theme: {
      icon: <DescriptionIcon sx={{ fontSize: 16 }} />,
      bg: "#E8F5E9",
      color: "#2E7D32",
    },
  },
  {
    keywords: ["PM", "amManagement"],
    theme: {
      icon: <HomeWorkIcon sx={{ fontSize: 16 }} />,
      bg: "#F3E5F5",
      color: "#7B1FA2",
    },
  },
  {
    keywords: ["BM", "bmManagement", "管理委託"],
    theme: {
      icon: <ApartmentIcon sx={{ fontSize: 16 }} />,
      bg: "#E8EAF6",
      color: "#3949AB",
    },
  },
  {
    keywords: ["管理"],
    theme: {
      icon: <HomeWorkIcon sx={{ fontSize: 16 }} />,
      bg: "#F3E5F5",
      color: "#7B1FA2",
    },
  },
  {
    keywords: ["設備"],
    theme: {
      icon: <ApartmentIcon sx={{ fontSize: 16 }} />,
      bg: "#E8EAF6",
      color: "#3949AB",
    },
  },
];

const DEFAULT_THEME: CategoryTheme = {
  icon: <MoreHorizIcon sx={{ fontSize: 16 }} />,
  bg: "#F5F5F5",
  color: "#666",
};

export const getCategoryTheme = (
  nameOrKey: string | undefined | null,
): CategoryTheme => {
  if (!nameOrKey) return DEFAULT_THEME;
  const k = String(nameOrKey).toLowerCase();
  for (const rule of THEME_RULES) {
    if (rule.keywords.some((kw) => k.includes(kw.toLowerCase()))) {
      return rule.theme;
    }
  }
  return DEFAULT_THEME;
};

/** @deprecated `getCategoryTheme(name)` を使用 */
export const CATEGORY_ICON_MAP: Record<
  string,
  { icon: React.ReactNode; bg: string; color: string }
> = {
  repair: {
    icon: <BuildIcon sx={{ fontSize: 16 }} />,
    bg: "#FFF3E0",
    color: "#E65100",
  },
  utility: {
    icon: <BoltIcon sx={{ fontSize: 16 }} />,
    bg: "#FFFDE7",
    color: "#F57F17",
  },
  tax: {
    icon: <AccountBalanceIcon sx={{ fontSize: 16 }} />,
    bg: "#E3F2FD",
    color: "#1565C0",
  },
  insurance: {
    icon: <DescriptionIcon sx={{ fontSize: 16 }} />,
    bg: "#E8F5E9",
    color: "#2E7D32",
  },
  amManagement: {
    icon: <HomeWorkIcon sx={{ fontSize: 16 }} />,
    bg: "#F3E5F5",
    color: "#7B1FA2",
  },
  bmManagement: {
    icon: <ApartmentIcon sx={{ fontSize: 16 }} />,
    bg: "#E8EAF6",
    color: "#3949AB",
  },
  // 旧 "management" キーの後方互換 (既存データ用)
  management: {
    icon: <HomeWorkIcon sx={{ fontSize: 16 }} />,
    bg: "#F3E5F5",
    color: "#7B1FA2",
  },
  other: {
    icon: <MoreHorizIcon sx={{ fontSize: 16 }} />,
    bg: "#F5F5F5",
    color: "#666",
  },
};

// ── 共通チップ ──
export const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  const c: Record<string, { bg: string; color: string }> = {
    // 完了系 — 緑
    支払済: { bg: "#E8F5E9", color: "#2E7D32" },
    // 進行系 (オレンジ)
    未払い: { bg: "#FFF3E0", color: "#E65100" },
    // 予定/AI 系 — 青系
    支払予定: { bg: "#E3F2FD", color: "#1565C0" },
    AI作成: { bg: "#DBF8FF", color: "#0E9DBC" },
    // 注意系
    要確認: { bg: "#FFF8E1", color: "#B45309" },
    // ニュートラル / 一時保留
    下書き: { bg: "#F5F5F5", color: "#666" },
    保留: { bg: "#F5F5F5", color: "#666" },
    その他: { bg: "#F5F5F5", color: "#666" },
    // 既存 (Recurring 等で使用)
    稼働中: { bg: "#E8F5E9", color: "#2E7D32" },
    廃止: { bg: "#F5F5F5", color: "#999" },
  };
  const v = c[status] || { bg: "#F5F5F5", color: "#666" };
  return (
    <CustomChip
      label={status}
      backgroundColor={v.bg}
      color={v.color}
      fontSize="12px"
      fontWeight={500}
      height={22}
    />
  );
};

export const TypeChip: React.FC<{ type: "CAPEX" | "OPEX" }> = ({ type }) => (
  <CustomChip
    label={type}
    backgroundColor="#F0F0F0"
    color="#666"
    fontSize="12px"
    fontWeight={600}
    height={20}
  />
);

// source (発生源) に応じたバッジ
export const SourceChip: React.FC<{ source?: string }> = ({ source }) => {
  if (!source || source === "direct") return null;
  const map: Record<string, { label: string; bg: string; color: string }> = {
    repair: { label: "工事由来", bg: "#FFF3E0", color: "#E65100" },
    loan: { label: "融資返済", bg: "#FFEBEE", color: "#C62828" },
    recurring: { label: "定期支出", bg: "#E0F7FA", color: "#00838F" },
  };
  const v = map[source];
  if (!v) return null;
  return (
    <CustomChip
      label={v.label}
      backgroundColor={v.bg}
      color={v.color}
      fontSize="12px"
      fontWeight={500}
      height={20}
    />
  );
};

export const fmt = (n: number) => `¥${n.toLocaleString("ja-JP")}`;
